/**
 * Structured data for the homepage: the Person (built from core/site
 * identity), the WebSite, the WebPage and the FAQ. The homepage's headline
 * facts for crawlers (100+ organisations, 96.5% detection accuracy) live here
 * and nowhere else in this module. English only.
 */
import { PERSON_NAME, PROFILE_IMAGE, SAME_AS, SITE_URL } from "../../core/site";

const PERSON_ID = `${SITE_URL}/#person`;
const WEBSITE_ID = `${SITE_URL}/#website`;

const faq = (name, text) => ({
	"@type": "Question",
	name,
	acceptedAnswer: { "@type": "Answer", text },
});

const person = {
	"@type": "Person",
	"@id": PERSON_ID,
	name: PERSON_NAME,
	url: SITE_URL,
	image: PROFILE_IMAGE,
	jobTitle: ["Co-Founder", "Machine Learning Engineer"],
	description:
		"Co-Founder of 2am. Machine Learning Engineer specialised in audio AI, LLMs, and production systems. Former Tech Lead ML at IRCAM Amplify, Sony CSL, Radio France.",
	knowsAbout: [
		"Machine Learning",
		"Audio AI",
		"LLMs",
		"System Architecture",
		"Embedded AI",
		"Real-time Inference",
		"Startup CTO",
		"Technical Due Diligence",
	],
	worksFor: { "@type": "Organization", name: "2am", url: "https://2am-music.com" },
	alumniOf: [
		{ "@type": "Organization", name: "IRCAM Amplify" },
		{ "@type": "Organization", name: "Sony CSL Music Team" },
		{ "@type": "Organization", name: "Radio France" },
		{ "@type": "EducationalOrganization", name: "Sorbonne Université" },
		{ "@type": "EducationalOrganization", name: "IMT Atlantique" },
	],
	sameAs: SAME_AS,
	address: { "@type": "PostalAddress", addressLocality: "Paris", addressCountry: "FR" },
};

const faqPage = {
	"@type": "FAQPage",
	mainEntity: [
		faq(
			`Who is ${PERSON_NAME}?`,
			`${PERSON_NAME} is a Machine Learning Engineer and Co-Founder/CTO of 2am, an AI company for live entertainment. She specialises in audio AI, LLMs, and production ML systems. Former Tech Lead ML at IRCAM Amplify, Sony CSL Music Team, and Radio France.`,
		),
		faq(
			`What has ${PERSON_NAME} built?`,
			"Production AI systems deployed at 100+ organisations: vocal separation, music classification, AI-generated audio detection (96.5% accuracy), spatial audio transformation, RAG systems for Radio France, and a GPT transformer trained on radio content. Currently building AI agents for live entertainment at 2am.",
		),
		faq(
			"What is 2am?",
			`2am is an AI startup co-founded by ${PERSON_NAME} (CTO) that builds real-time AI agents and audience intelligence systems for the live entertainment industry, founded in 2024 in Paris.`,
		),
		faq(
			`What is ${PERSON_NAME}'s expertise?`,
			"Audio AI (source separation, FM synthesis, speech detection), LLMs and production AI systems, real-time ML inference, ML infrastructure and team building, technical due diligence, and startup CTO.",
		),
	],
};

/** JSON-LD @graph for "/": Person, WebSite, WebPage and FAQPage. */
export const HOME_JSON_LD = {
	"@context": "https://schema.org",
	"@graph": [
		person,
		{
			"@type": "WebSite",
			"@id": WEBSITE_ID,
			url: SITE_URL,
			name: PERSON_NAME,
			description: `Portfolio of ${PERSON_NAME} - CTO, ML Engineer, Co-Founder`,
			publisher: { "@id": PERSON_ID },
			inLanguage: "en",
		},
		{
			"@type": "WebPage",
			"@id": `${SITE_URL}/#webpage`,
			url: SITE_URL,
			name: `${PERSON_NAME} - CTO · AI Systems · Co-Founder`,
			description:
				"I build AI systems that work outside the lab. Co-founder and CTO of 2am. Formerly at IRCAM Amplify (Tech Lead ML), Radio France and Sony CSL.",
			isPartOf: { "@id": WEBSITE_ID },
			about: { "@id": PERSON_ID },
			inLanguage: "en",
		},
		faqPage,
	],
};
